import React, { useEffect, useState } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import {
  Navbar,
  PrivacyPolicy,
  TermsnConditions,
  Disclaimer,
  ShippingandDelivery,
  ReturnPolicy,
  Error404,
  Home,
  Aboutus,
  ReportABug,
  Contactus,
  ProfileEdit,
  Register,
  Signin,
  Orders,
  OrderDetails,
  Cart,
  Checkout,
  DirectPayment,
  PaymentSuccess,
  ForgotPassword,
  Dashboard,
  Store,
  SubCatalogueList,
  ProductList,
  ProductDetails,
} from "./routes/Routes";

import {
  AdminDashboard,
  AdminNavbar,
  AllOrders,
  Media,
  ProductsCatalog,
  AdminProductList,
  ProductsSubCat,
  ProductsVariants,
  UserDetails,
  Users,
  BulkUpload,
  SingleProduct_Uplaod,
  EditProduct,
  SettingsPage,
  AdminOrderDetails,
  Inventory,
} from "./routes/AdminRoutes";
import { useAuth } from "./hooks/GlobalProvider";
import LogoLoader from "./components/LogoLoader/LogoLoader";
import { ProtectedRoute, AdminRoute } from "./hooks/PrivateRoute";

// Route configuration with all routes in one array
const routes = [
  // Public routes
  { path: "/", element: <Home />, public: true },
  { path: "/aboutus", element: <Aboutus />, public: true },
  { path: "/reportbug", element: <ReportABug />, public: true },
  { path: "/contactus", element: <Contactus />, public: true },
  { path: "/privacypolicy", element: <PrivacyPolicy />, public: true },
  { path: "/termsconditions", element: <TermsnConditions />, public: true },
  { path: "/returnpolicy", element: <ReturnPolicy />, public: true },
  { path: "/shippingdelivery", element: <ShippingandDelivery />, public: true },
  { path: "/disclaimer", element: <Disclaimer />, public: true },
  { path: "/signin", element: <Signin />, public: true },
  { path: "/signup", element: <Register />, public: true },
  { path: "/forgot-password", element: <ForgotPassword />, public: true },

  // Store public routes
  { path: "/Store", element: <Store />, public: true },
  {
    path: "/Store/:catalogueName/:catalogueId",
    element: <SubCatalogueList />,
    public: true,
  },
  {
    path: "/Store/sub-catalogue/products/:subCatalogueId",
    element: <ProductList />,
    public: true,
  },
  {
    path: "/Store/sub-catalogue/products/product-details/:productId",
    element: <ProductDetails />,
    public: true,
  },

  // Protected routes (regular users)
  {
    path: "/user/dashboard",
    element: <Dashboard />,
    public: false,
    backend: false,
  },
  {
    path: "/user/profileedit",
    element: <ProfileEdit />,
    public: false,
    backend: false,
  },
  {
    path: "/Store/cart",
    element: <Cart />,
    public: false,
    backend: false,
  },
    {
    path: "/Store/checkout/DirectPayment",
    element: <DirectPayment />,
    public: false,
    backend: false,
  },
  {
    path: "/Store/checkout",
    element: <Checkout />,
    public: false,
    backend: false,
  },

  {
    path: "/Store/checkout/paymentsuccess",
    element: <PaymentSuccess />,
    public: false,
    backend: false,
  },
  {
    path: "/user/orders",
    element: <Orders />,
    public: false,
    backend: false,
  },
  {
    path: "/user/orders/orderdetails",
    element: <OrderDetails />,
    public: false,
    backend: false,
  },

  // Admin routes (backend users only)
  {
    path: "/auth/dashboard",
    element: <AdminDashboard />,
    public: false,
    backend: true,
  },
  {
    path: "/auth/media",
    element: <Media />,
    public: false,
    backend: true,
  },
  {
    path: "/auth/users",
    element: <Users />,
    public: false,
    backend: true,
  },
  {
    path: "/auth/users/user-details/:userId",
    element: <UserDetails />,
    public: false,
    backend: true,
  },
  {
    path: "/auth/all-orders",
    element: <AllOrders />,
    public: false,
    backend: true,
  },
  {
    path: "/auth/order-details/:orderId",
    element: <AdminOrderDetails />,
    public: false,
    backend: true,
  },
  {
    path: "/auth/inventory",
    element: <Inventory />,
    public: false,
    backend: true,
  },
  {
    path: "/auth/A_product_catalogue/product_catalogue",
    element: <ProductsCatalog />,
    public: false,
    backend: true,
  },
  {
    path: "/auth/B_product_sub_catalogue/product_sub_catalogue",
    element: <ProductsSubCat />,
    public: false,
    backend: true,
  },
  {
    path: "/auth/D_product_variants/add_product_variant",
    element: <ProductsVariants />,
    public: false,
    backend: true,
  },
  {
    path: "/auth/ProductList",
    element: <AdminProductList />,
    public: false,
    backend: true,
  },
  {
    path: "/auth/bulk-upload",
    element: <BulkUpload />,
    public: false,
    backend: true,
  },
  {
    path: "/auth/add-product",
    element: <SingleProduct_Uplaod />,
    public: false,
    backend: true,
  },
  {
    path: "/auth/edit-product/:productId",
    element: <EditProduct />,
    public: false,
    backend: true,
  },
  {
    path: "/auth/settings",
    element: <SettingsPage />,
    public: false,
    backend: true,
  },

  // Catch-all route
  { path: "/*", element: <Error404 />, public: true },
];

const App = () => {
  const location = useLocation();
  const { user, loading } = useAuth();
  const [cart, setCart] = useState([]);

  const hideNavbarRoutes = ["/Store/checkout"];

  // Check if current route is an backend route
  const isAdminRoute = location.pathname.startsWith("/auth");

  // Cart management
  useEffect(() => {
    const savedCart = JSON.parse(localStorage.getItem("cart")) || [];
    setCart(savedCart);
  }, []);

  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));
  }, [cart]);

  if (loading) {
    return <LogoLoader />;
  }

  return (
    <>
      {/* Show AdminNavbar for backend routes */}
      {isAdminRoute && <AdminNavbar />}

      {/* Show regular Navbar for non-backend, non-hidden routes */}
      {!isAdminRoute && !hideNavbarRoutes.includes(location.pathname) && (
        <Navbar />
      )}

      {/* Main content with conditional padding for admin routes */}
      <div className={`min-h-screen ${isAdminRoute ? "md:ml-52" : ""}`}>
        <Routes location={location} key={location.pathname}>
          {routes.map((route, index) => (
            <Route
              key={index}
              path={route.path}
              element={
                route.public ? (
                  route.element
                ) : route.backend ? (
                  <AdminRoute>{route.element}</AdminRoute>
                ) : (
                  <ProtectedRoute>{route.element}</ProtectedRoute>
                )
              }
            />
          ))}
        </Routes>
      </div>
    </>
  );
};

export default App;
